import { useCallback, useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import moment from 'moment/moment'
import _ from 'underscore'

import Search from 'components/Search'
import Spinner from 'components/Spinner'
import SearchHistory from 'components/SearchHistory'

import useDeviceSearch from 'hooks/useDeviceSearch'
import useFavorites from 'hooks/useFavorites'
import useSearchHistory from 'hooks/useSearchHistory'
import useSearchParams from 'hooks/useSearchParams'
import { useSessionContext } from 'hooks/SessionContext'

import MultipleDevicesResults from 'pages/DeviceSearch/MultipleDevicesResults'

import URLParamsHelper from 'helpers/url_params_helper'

import Api from 'services/api'

import { SinglePanePage, PageTitle, PageHeader, screenSize } from 'styles/shared'

const DeviceSearch = () => {
  const SEARCH_HISTORY_TYPE = 'devices'

  const location = useLocation()
  const navigate = useNavigate()

  const { id: deviceId } = useParams() || ''
  const deviceQuery = localStorage.getItem('devicesQuery')
  const [urlUpdate, setUrlUpdate] = useState(false)

  const queryParam = useSearchParams('q')
  const queryText = deviceId || queryParam || deviceQuery || ''

  const parseDeviceIds = useCallback((deviceIdStr) => {
    if (!deviceIdStr || deviceIdStr.trim().length === 0) { return [] }

    return _.uniq(_.escape(deviceIdStr.replace(/[,\s]+/g, ' ').trim()).split(' ')).sort()
  }, [])

  const [query, setQuery] = useState(parseDeviceIds(queryText).join(', '))
  const [deviceIds, setDeviceIds] = useState(parseDeviceIds(queryText))
  const [showSpinner, setShowSpinner] = useState(false)
  const [refreshTimestamp, setRefreshTimestamp] = useState(moment().valueOf())

  const { session: { userId } } = useSessionContext()
  const { searchHistory, isSearchHistoryLoaded } = useSearchHistory(userId, SEARCH_HISTORY_TYPE, refreshTimestamp)
  const { devices, invalidDeviceIds, areDevicesLoaded } = useDeviceSearch(deviceIds)
  const [isSearchHistoryShown, setIsSearchHistoryShown] = useState(false)

  if (isSearchHistoryLoaded && !isSearchHistoryShown) {
    setIsSearchHistoryShown(true)
  }

  useFavorites(userId)

  const hasQueryChanged = useCallback((newQuery) => {
    const newQueriedDeviceIds = parseDeviceIds(newQuery) || []

    return deviceIds.length !== newQueriedDeviceIds.length || _.difference(newQueriedDeviceIds, deviceIds).length
  }, [deviceIds, parseDeviceIds])

  const updateSearchHistory = useCallback(async (deviceIds) => {
    if (!deviceIds || deviceIds.length === 0) {
      return
    }
    try {
      const query = deviceIds.join(', ')
      await Api.SearchHistory.modify(userId, SEARCH_HISTORY_TYPE, query)
      setRefreshTimestamp(moment().valueOf())
    } catch {

    }
  }, [userId])

  const updateSearch = useCallback(async (newQuery) => {
    setQuery(newQuery)

    const newQueriedDeviceIds = parseDeviceIds(newQuery)

    localStorage.setItem('devicesQuery', newQuery)

    setDeviceIds(newQueriedDeviceIds)

    const priorSelectedDeviceIds = parseDeviceIds(localStorage.getItem('devicesSelected'))

    localStorage.setItem('devicesSelected', _.intersection(priorSelectedDeviceIds, newQueriedDeviceIds).join(','))

    await updateSearchHistory(newQueriedDeviceIds)
  }, [parseDeviceIds, updateSearchHistory])

  const onSearch = useCallback((newQuery) => {
    if (!hasQueryChanged(newQuery)) {
      return
    }

    updateSearch(newQuery)

    const newQueriedDeviceIds = parseDeviceIds(newQuery)
    let path

    if (newQueriedDeviceIds?.length === 1) {
      const deviceId = newQueriedDeviceIds[0]
      path = `/devices/${deviceId}/dashboard`
    } else if (newQueriedDeviceIds?.length > 1) {
      path = `/devices?q=${newQueriedDeviceIds.join(',')}`
    } else {
      path = '/devices'
    }
    navigate(path)
  }, [navigate, hasQueryChanged, parseDeviceIds, updateSearch])

  useEffect(() => {
    updateSearchHistory(deviceIds)
  }, [queryText, location.state, deviceIds]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!urlUpdate && deviceQuery && parseDeviceIds(queryParam) !== parseDeviceIds(deviceQuery)) {
      if (deviceIds?.length === 1) {
        const activeTabSegment = location.pathname.split('/devices/' + deviceIds[0] + '/')[1]
        const tabPath = activeTabSegment ? '/' + activeTabSegment : ''
        const search = location.pathname.startsWith('/devices/' + deviceIds[0]) ? location.search : ''
        window.history.replaceState({}, '', '/devices/' + deviceIds[0] + tabPath + search)
      } else if (deviceIds?.length > 1) {
        URLParamsHelper.update('q', parseDeviceIds(queryText).join(','))
      }
      setUrlUpdate(true)
    }
  }, [urlUpdate, deviceQuery]) // eslint-disable-line react-hooks/exhaustive-deps

  const onClickClearIcon = () => {
    setShowSpinner(false)
    setQuery('')
    onSearch('')
  }

  return (
    <>
      <PageHeader>
        <PageTitle>Device Search</PageTitle>
        <S.Search
          focusOnMount
          ariaLabel="device search"
          name="deviceId"
          placeholder="Device AUID or Serial Number"
          helperText="Search against a single AUID / Serial Number or enter multiple values comma separated."
          value={query}
          onChange={setQuery}
          onSearch={onSearch}
          onClickClearIcon={onClickClearIcon}
          showSpinner={showSpinner}
        />
      </PageHeader>

      <S.SinglePanePage>
        { isSearchHistoryShown &&
          <SearchHistory
            onClickCallBack={onSearch}
            type={SEARCH_HISTORY_TYPE}
            query={query}
            searchHistory={searchHistory}
          />
        }

        { (!isSearchHistoryShown && !areDevicesLoaded) && <Spinner variant="page" /> }

        { isSearchHistoryShown && areDevicesLoaded &&
          <MultipleDevicesResults
            pathRoot="devices"
            {...{ devices, invalidDeviceIds }}
          />
        }
      </S.SinglePanePage>
    </>

  )
}

export default DeviceSearch

const S = {
  Search: styled(Search)`
    max-width: 500px;
    
    @media ${screenSize.small} {
      display: flex;
      flex-direction: column;
    }
  `,
  SinglePanePage: styled(SinglePanePage)`
     @media ${screenSize.small} {
        margin-top: 0px;
     }
  `
}
